import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { journalsApi, releasesApi } from '../../api/endpoints';
import { FileField, FormActions, FormSection, SingleSelect, TextInput } from '../../components/Form';
import { useUi } from '../../context/UiContext';
import { listUrl } from '../../hooks/listReturn';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import styles from '../listPage.module.css';

const EMPTY = {
    journals_id: '',
    number: '',
    tome: '',
    issn: '',
    date: '',
};

const ReleaseForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const { toast } = useUi();
    const isEdit = Boolean(id);

    const [values, setValues] = useState(EMPTY);
    const [saved, setSaved] = useState(EMPTY); // для проверки «есть ли несохранённое»
    const leavingRef = useRef(false);
    const [cover, setCover] = useState(null);
    const [document, setDocument] = useState(null);
    const [current, setCurrent] = useState({ file_path: null, document: null });

    const [journals, setJournals] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [loadError, setLoadError] = useState(null);

    const dirty = useMemo(
        () => !loading && (cover !== null || document !== null || JSON.stringify(values) !== JSON.stringify(saved)),
        [loading, values, saved, cover, document]
    );

    const set = useCallback((key, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    }, []);

    useEffect(() => {
        let cancelled = false;
        journalsApi
            .list({ perPage: 200, sort: 'name', dir: 'asc' })
            .then((page) => {
                if (cancelled) return;
                setJournals(page.items.map((j) => ({ value: j.id, label: j.name })));
            })
            .catch((e) => !cancelled && toast.error(`Не удалось загрузить журналы: ${e.message}`));
        return () => {
            cancelled = true;
        };
    }, [toast]);

    useEffect(() => {
        if (!isEdit) return undefined;

        let cancelled = false;
        setLoading(true);

        releasesApi
            .get(id)
            .then((item) => {
                if (cancelled) return;
                const loaded = {
                    // Прежняя форма не подставляла ни журнал, ни дату.
                    journals_id: item.journals_id || '',
                    number: item.number || '',
                    tome: item.tome || '',
                    issn: item.issn || '',
                    date: item.date || '',
                };
                setValues(loaded);
                setSaved(loaded);
                setCurrent({ file_path: item.file_path || null, document: item.document || null });
            })
            .catch((e) => !cancelled && setLoadError(e))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSaving(true);
        setProgress(0);

        const payload = { ...values };
        if (cover) payload.file = cover;
        if (document) payload.document = document;

        try {
            if (isEdit) {
                await releasesApi.update(id, payload, setProgress);
                toast.success('Изменения сохранены');
                setSaved(values);
                setCover(null);
                setDocument(null);
                const fresh = await releasesApi.get(id);
                setCurrent({ file_path: fresh.file_path || null, document: fresh.document || null });
            } else {
                const created = await releasesApi.create(payload, setProgress);
                toast.success('Выпуск добавлен');
                leavingRef.current = true;
                history.push(`/admin/releases/${created.id}`);
            }
        } catch (err) {
            if (err.isValidation) {
                setErrors(err.errors || {});
                toast.error('Проверьте заполнение полей');
            } else {
                toast.error(err.message);
            }
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    if (loadError) {
        return (
            <div className="adm-card adm-card-body">
                <p>Не удалось загрузить выпуск: {loadError.message}</p>
                <NavLink to={listUrl('/admin/releases')} className="adm-btn adm-btn-secondary">
                    К списку выпусков
                </NavLink>
            </div>
        );
    }

    return (
        <div className={styles.formPage}>
            <UnsavedChangesPrompt when={dirty && !saving} bypassRef={leavingRef} />
            <NavLink to={listUrl('/admin/releases')} className={styles.backLink}>
                ← К списку выпусков
            </NavLink>

            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">{isEdit ? 'Редактирование выпуска' : 'Новый выпуск'}</h1>
                    {isEdit && values.number && (
                        <p className="adm-page-subtitle">№{values.number}</p>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="adm-card">
                    <div className={styles.formLoading}>Загрузка данных выпуска…</div>
                </div>
            ) : (
                <form className="adm-card" onSubmit={handleSubmit}>
                    <FormSection title="Основное">
                        <SingleSelect
                            label="Журнал"
                            required
                            options={journals}
                            value={values.journals_id}
                            onChange={(v) => set('journals_id', v)}
                            error={errors.journals_id && errors.journals_id[0]}
                        />

                        <div className="adm-grid-2">
                            <TextInput
                                id="rel-number"
                                label="Номер"
                                value={values.number}
                                onChange={(e) => set('number', e.target.value)}
                                error={errors.number && errors.number[0]}
                            />
                            <TextInput
                                id="rel-tome"
                                label="Том"
                                value={values.tome}
                                onChange={(e) => set('tome', e.target.value)}
                                error={errors.tome && errors.tome[0]}
                            />
                        </div>

                        <div className="adm-grid-2">
                            <TextInput
                                id="rel-issn"
                                label="ISSN"
                                value={values.issn}
                                onChange={(e) => set('issn', e.target.value)}
                                error={errors.issn && errors.issn[0]}
                            />
                            <TextInput
                                id="rel-date"
                                type="date"
                                label="Дата выпуска"
                                value={values.date}
                                onChange={(e) => set('date', e.target.value)}
                                error={errors.date && errors.date[0]}
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Файлы">
                        <FileField
                            id="rel-cover"
                            label="Обложка"
                            accept="image/*"
                            currentLabel="Текущая обложка"
                            currentPath={current.file_path}
                            file={cover}
                            onChange={setCover}
                            error={errors.file && errors.file[0]}
                        />

                        <FileField
                            id="rel-document"
                            label="Сборник (PDF)"
                            accept="application/pdf"
                            currentLabel="Текущий сборник"
                            currentPath={current.document}
                            file={document}
                            onChange={setDocument}
                            progress={progress}
                            error={errors.document && errors.document[0]}
                        />
                    </FormSection>

                    <FormActions>
                        <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                            {saving && <span className="adm-spinner" />}
                            {saving ? 'Сохранение…' : isEdit ? 'Сохранить изменения' : 'Добавить выпуск'}
                        </button>
                        <NavLink to={listUrl('/admin/releases')} className="adm-btn adm-btn-secondary">
                            Отмена
                        </NavLink>
                        {saving && progress > 0 && progress < 100 && (
                            <span className="adm-muted">Загрузка файла: {progress}%</span>
                        )}
                    </FormActions>
                </form>
            )}
        </div>
    );
};

export default ReleaseForm;
